import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { MailIcon, LockIcon, UserIcon, BookOpenIcon, StoreIcon, ArrowRightIcon, GlobeIcon, UsersIcon } from 'lucide-react';
const Register = () => {
  const dk = useDarkClasses();
  const navigate = useNavigate();
  const [role, setRole] = useState<'reader' | 'vendor'>('reader');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate registration
    setTimeout(() => {
      setIsLoading(false);
      navigate('/dashboard');
    }, 1500);
  };
  return <div className={`min-h-screen flex ${dk.page}`} data-id="element-6874" data-mp="3q3wfbn1hu2ez">
      {/* Left Panel - Editorial/Brand */}
      <div className="hidden md:flex w-1/2 bg-slate-900 text-white flex-col justify-between p-12 lg:p-16" data-id="element-6875" data-mp="3b6x2nkrsbjdn">
        <div data-id="element-6876" data-mp="3g5qhbgit5gfe">
          <div className="flex items-center gap-3 mb-2" data-id="element-6877" data-mp="3g56wns1rwdxo">
            <BookOpenIcon className="h-8 w-8 text-indigo-400" data-id="element-6878" data-mp="3g7tdyjegu7t3" />
            <span className="text-2xl font-bold tracking-tight" data-id="element-6879" data-mp="7rhvcrasjnw7">LitLaunch</span>
          </div>
        </div>

        <div className="max-w-md" data-id="element-6880" data-mp="coq98fowp5rn">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight" data-id="element-6881" data-mp="3u5rdiio4nzoj">
            Join 50,000+ readers and authors.
          </h1>
          <p className="text-lg text-slate-300 leading-relaxed" data-id="element-6882" data-mp="2yexi6yzdsa7">
            Whether you're here to discover your next obsession or share your
            stories with the world — you're in the right place.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6" data-id="element-6883" data-mp="vhu94ispweup">
          <div data-id="element-6884" data-mp="15bztvkcjpqup">
            <p className="text-2xl font-bold text-white" data-id="element-6885" data-mp="15aomlajw60yu">10k+</p>
            <p className="text-xs text-slate-400 uppercase tracking-wide mt-1" data-id="element-6886" data-mp="10cwo1xoqrglo">
              Indie Authors
            </p>
          </div>
          <div data-id="element-6887" data-mp="w9qiks9gdtg9">
            <p className="text-2xl font-bold text-white" data-id="element-6888" data-mp="w98fpcsacgzd">100k+</p>
            <p className="text-xs text-slate-400 uppercase tracking-wide mt-1" data-id="element-6889" data-mp="rax4oyq980fk">
              Books
            </p>
          </div>
          <div data-id="element-6890" data-mp="me6p0cid36uo">
            <p className="text-2xl font-bold text-white" data-id="element-6891" data-mp="meq8vyrko5bc">150+</p>
            <p className="text-xs text-slate-400 uppercase tracking-wide mt-1" data-id="element-6892" data-mp="hldndxi6wemt">
              Countries
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className={`flex-1 flex items-center justify-center p-8 ${dk.surface}`} data-id="element-6893" data-mp="8ms6e6sd97o5">
        <div className="max-w-md w-full space-y-8" data-id="element-6894" data-mp="3l2telll2jxf">
          <div className="text-center md:text-left" data-id="element-6895" data-mp="3usp24sn5tcl5">
            <div className="flex justify-center md:justify-start mb-6 md:hidden" data-id="element-6896" data-mp="3ut78ii3jd76s">
              <BookOpenIcon className="h-10 w-10 text-indigo-600" data-id="element-6897" data-mp="2a9lbtxysmv0s" />
            </div>
            <h2 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-6898" data-mp="2f8y8fafgvggk">
              Create your account
            </h2>
            <p className={`mt-2 text-sm ${dk.textMuted}`} data-id="element-6899" data-mp="2ka5glaliuhnr">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors" data-id="element-6900" data-mp="2o94jyqrlu6d3">
                Sign in
              </Link>
            </p>
          </div>

          {/* Role Selection */}
          <div className="grid grid-cols-2 gap-4 mt-6" data-id="element-6901" data-mp="20kbh0ackey8z">
            <button type="button" onClick={() => setRole('reader')} className={`p-4 border rounded-xl flex flex-col items-center justify-center transition-all duration-200 ${role === 'reader' ? 'border-indigo-600 bg-indigo-50/50 text-indigo-700 ring-1 ring-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-300' : `${dk.card} ${dk.textSecondary} hover:border-indigo-300 dark:hover:border-indigo-700`}`} data-id="element-6902" data-mp="20iqmulu5xhbn">
              <BookOpenIcon className={`h-8 w-8 mb-3 ${role === 'reader' ? 'text-indigo-600 dark:text-indigo-400' : dk.textMuted}`} data-id="element-6903" data-mp="oslwjo11j8ps" />
              <span className="font-semibold" data-id="element-6904" data-mp="ypqk9sajbc8p">Reader</span>
              <span className="text-xs mt-1 opacity-70" data-id="element-6905" data-mp="yo5pc1kb5rab">
                I want to discover books
              </span>
            </button>
            <button type="button" onClick={() => setRole('vendor')} className={`p-4 border rounded-xl flex flex-col items-center justify-center transition-all duration-200 ${role === 'vendor' ? 'border-purple-600 bg-purple-50/50 text-purple-700 ring-1 ring-purple-600 dark:bg-purple-900/20 dark:text-purple-300' : `${dk.card} ${dk.textSecondary} hover:border-purple-300 dark:hover:border-purple-700`}`} data-id="element-6906" data-mp="17i29jatwwtn3">
              <StoreIcon className={`h-8 w-8 mb-3 ${role === 'vendor' ? 'text-purple-600 dark:text-purple-400' : dk.textMuted}`} data-id="element-6907" data-mp="3j9fqd0rf6fdb" />
              <span className="font-semibold" data-id="element-6908" data-mp="23owxxyuu25e8">Vendor</span>
              <span className="text-xs mt-1 opacity-70" data-id="element-6909" data-mp="23qggjw3yosj6">
                I want to sell books/services
              </span>
            </button>
          </div>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit} data-id="element-6910" data-mp="1txj4o8th61tj">
            <div data-id="element-6911" data-mp="1fyjhl5lm1uzh">
              <label htmlFor="name" className={`block text-sm font-medium ${dk.textPrimary} mb-1.5`} data-id="element-6912" data-mp="1fxzwxh4ksshr">
                Full Name
              </label>
              <div className="relative" data-id="element-6913" data-mp="161wx4hx755xz">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6914" data-mp="162zo0puir8dw">
                  <UserIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-6915" data-mp="1b22jbikp608o" />
                </div>
                <input id="name" name="name" type="text" required value={formData.name} onChange={handleChange} className={`block w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors ${dk.input}`} placeholder="Jane Doe" data-id="element-6916" data-mp="30fpjhc0jcpne" />
              </div>
            </div>

            <div data-id="element-6917" data-mp="2p14a3zi42ygv">
              <label htmlFor="email" className={`block text-sm font-medium ${dk.textPrimary} mb-1.5`} data-id="element-6918" data-mp="2p1nurnz5c0yl">
                Email address
              </label>
              <div className="relative" data-id="element-6919" data-mp="3hqkxujusdr6r">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6920" data-mp="3cwqifsm7rydh">
                  <MailIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-6921" data-mp="37z712ju7z5hb" />
                </div>
                <input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleChange} className={`block w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors ${dk.input}`} placeholder="you@example.com" data-id="element-6922" data-mp="98yhp9o9evr7" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-6923" data-mp="qroxxnnr7edt">
              <div data-id="element-6924" data-mp="vq07rye1tow1">
                <label htmlFor="password" className={`block text-sm font-medium ${dk.textPrimary} mb-1.5`} data-id="element-6925" data-mp="vofai10cnruh">
                  Password
                </label>
                <div className="relative" data-id="element-6926" data-mp="14iajjk4le2fe">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6927" data-mp="19gc1g1qa7vpa">
                    <LockIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-6928" data-mp="1eg86m4aptt6f" />
                  </div>
                  <input id="password" name="password" type="password" required value={formData.password} onChange={handleChange} className={`block w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors ${dk.input}`} placeholder="••••••••" data-id="element-6929" data-mp="1jejewald3vmj" />
                </div>
              </div>

              <div data-id="element-6930" data-mp="1l2sc2vm93crc">
                <label htmlFor="confirmPassword" className={`block text-sm font-medium ${dk.textPrimary} mb-1.5`} data-id="element-6931" data-mp="1l2a6379sdk68">
                  Confirm
                </label>
                <div className="relative" data-id="element-6932" data-mp="1utn1u9ypguhj">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6933" data-mp="1q0li9onk06rm">
                    <LockIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-6934" data-mp="12ahijsdhkr5w" />
                  </div>
                  <input id="confirmPassword" name="confirmPassword" type="password" required value={formData.confirmPassword} onChange={handleChange} className={`block w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors ${dk.input}`} placeholder="••••••••" data-id="element-6935" data-mp="xcy34p51y8cd" />
                </div>
              </div>
            </div>

            <div className="flex items-start pt-2" data-id="element-6936" data-mp="3slo23z54w7fn">
              <div className="flex items-center h-5" data-id="element-6937" data-mp="iewa053kvby">
                <input id="termsAccepted" name="termsAccepted" type="checkbox" required checked={formData.termsAccepted} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-6938" data-mp="3ipb9z5p8m1n0" />
              </div>
              <div className="ml-3 text-sm" data-id="element-6939" data-mp="2c6wes0muhqd7">
                <label htmlFor="termsAccepted" className={`font-medium ${dk.textSecondary}`} data-id="element-6940" data-mp="2n0f98b3karvw">
                  I agree to the{' '}
                  <Link to="/terms" className="text-indigo-600 hover:text-indigo-500 underline" data-id="element-6941" data-mp="2dacikhjwxq0q">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-indigo-600 hover:text-indigo-500 underline" data-id="element-6942" data-mp="24az8bmnysxey">
                    Privacy Policy
                  </Link>
                </label>
              </div>
            </div>

            <button type="submit" disabled={isLoading} className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md ${role === 'reader' ? 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500' : 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500'}`} data-id="element-6943" data-mp="1zabgiyrwwcjo">
              {isLoading ? <span className="flex items-center" data-id="element-6944" data-mp="1mz0mav869esz">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-6945" data-mp="1mxh6f5qfh5rq">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-6946" data-mp="2apo8mliu46z2"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-6947" data-mp="bc0n70lkgrdi"></path>
                  </svg>
                  Creating Account...
                </span> : <span className="flex items-center" data-id="element-6948" data-mp="1a2xiub7izrp9">
                  Create Account
                  <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-6949" data-mp="1f18qcfaclq4b" />
                </span>}
            </button>
          </form>
        </div>
      </div>
    </div>;
};
export default Register;